/**
 * Aviso global de sesión expirada (bug fila 100, UAT 2026-08-25). `getToken()`/`acquireTokenSilentSafe()`
 * ya intentan recuperarse solos con un popup antes de rendirse — esto es el respaldo para cuando el
 * popup también falla (bloqueado, cerrado): un evento simple en `window` para que UN banner global
 * (montado una sola vez en `AppRouter`) avise, sin que cada pantalla tenga que importar y manejar
 * `SessionExpiredError` por su cuenta. Las 4 pantallas de "Traducir auto." siguen manejándolo también
 * de forma explícita (mensaje más específico); esto es la red de seguridad para todo lo demás.
 */
import { useEffect, useState } from 'react';

const EVENT_NAME = 'pmp:session-expired';

export function notifySessionExpired(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(EVENT_NAME));
}

export function onSessionExpired(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {/* empty */};
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

/**
 * Sesión expirada persistente para toda la app, no solo para el banner: cuando
 * `acquireTokenSilentSafe`/`resolveAuthToken` se rinden, `getCurrentUserProfile` atrapa ese error
 * (junto con cualquier otro) y devuelve un perfil de emergencia "solo Pulso" (`DEFAULT_MODULES` en
 * `api/client.ts`, `FALLBACK_MODULES` en `useCurrentUser.ts`) — indistinguible de un usuario real
 * sin Brillamos. Los guards `goToPulsoPortal` de `AppLayout`/`LandingDecision` leían ese perfil de
 * emergencia como si fuera legítimo y sacaban al usuario de Reconocimiento con un
 * `window.location.replace('/')`, sin aviso, cada vez que la sesión expiraba a mitad de uso
 * (bug reportado 2026-08-31: la sesión expira rápido y siempre cae en Pulso). Este hook expone el
 * mismo evento que ya dispara el banner para que esos guards se desactiven mientras la sesión esté
 * caída, en vez de navegar con datos de perfil que no son reales. No se autodescarta (mismo criterio
 * que el banner): una vez expirada, sigue expirada hasta el siguiente login real.
 */
export function useIsSessionExpired(): boolean {
  const [expired, setExpired] = useState(false);
  useEffect(() => onSessionExpired(() => setExpired(true)), []);
  return expired;
}
